import { I18nProvider, Refine } from "@refinedev/core";
import { useNotificationProvider } from "@refinedev/mui";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import routerBindings, {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { BrowserRouter } from "react-router-dom";
import { ColorModeContextProvider } from "./providers/colorProvider";
import SyncrosaleRouter from "./router/SyncrosaleRouter";
import { useTranslation } from "react-i18next";
import { dataProvider } from "./providers/dataProvider";
import { useKeycloak } from "@react-keycloak/web";
import { createKeycloakProvider } from "./providers/authProvider";
import { TOptionsBase } from "i18next";
import { Box } from "@mui/material";
import { getResources } from "./router/navigationItems";
import { useMarketplaceStore } from "./store/useMarketplaceStore";
import { getAdminResources } from "./router/adminNavigationItems";
import { getAdminAccess } from "./utils/adminAccess";
import { SyncrosaleLoader } from "./components/layout/components/SyncrosaleLoader";
import { HolidayBanner } from "./components/layout/HolidayBanner";
import { NetworkHealthBanner } from "./components/layout/NetworkHealthBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RefineKbarProvider } from "@refinedev/kbar";

function App() {
  const { t, i18n } = useTranslation();
  const { keycloak, initialized } = useKeycloak();
  const notificationProvider = useNotificationProvider();
  // Set once the seller's marketplaces are loaded (see useInitializeApp).
  const hasStore = useMarketplaceStore((state) => state.countries.length > 0);

  if (!initialized) {
    return <SyncrosaleLoader />;
  }

  // Admin users get the admin group on top of the regular seller menu; an admin
  // without a store only sees the admin group.
  const adminAccess = getAdminAccess(keycloak.tokenParsed);
  const { isAdmin } = adminAccess;

  const i18nProvider: I18nProvider = {
    translate: (key: string, params?: TOptionsBase & Record<string, unknown>) =>
      t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  return (
    <Box
      lang={
        // This provides the current language to whole app
        // Especially for mui buttons capitalization that was wrong with Turkish
        i18nProvider.getLocale()
      }
    >
      <ErrorBoundary>
        <BrowserRouter>
          <ColorModeContextProvider>
            <RefineKbarProvider>
              <CssBaseline />
              <GlobalStyles
                styles={{ html: { WebkitFontSmoothing: "auto" } }}
              />
              <HolidayBanner />
              <NetworkHealthBanner />
              <Refine
                notificationProvider={notificationProvider}
                dataProvider={dataProvider}
                routerProvider={routerBindings}
                authProvider={createKeycloakProvider(keycloak)}
                i18nProvider={i18nProvider}
                resources={
                  isAdmin
                    ? [
                        ...getAdminResources(adminAccess),
                        ...(hasStore ? getResources() : []),
                      ]
                    : getResources()
                }
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  useNewQueryKeys: true,
                }}
              >
                <SyncrosaleRouter />
                <UnsavedChangesNotifier />
                <DocumentTitleHandler />
                {/* <ReactQueryDevtools initialIsOpen={false} /> */}
              </Refine>
            </RefineKbarProvider>
          </ColorModeContextProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </Box>
  );
}

export default App;
